import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Center,
  Text,
  Badge,
  useColorModeValue,
  Box,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { BellIcon, WarningIcon, TimeIcon } from "@chakra-ui/icons";
import axios from "axios";
import { decryptData, URL_WEB_BACKEND } from "CustomUtil";

export default function KenaikanGajiTable() {
  const authdata = decryptData(localStorage.getItem("authinfosemakinpas"));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const bgBox = useColorModeValue("white", "gray.800");
  const bgHead = useColorModeValue("green.600", "green.500");
  const bgAlert = useColorModeValue("green.50", "green.900");
  const hoverBg = useColorModeValue("green.50", "green.900");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${URL_WEB_BACKEND}/kepegawaians/daftarKenaikanGaji`,
          {
            headers: { Authorization: "Bearer " + authdata },
          }
        );

        console.log("Response Kenaikan Gaji:", res.data);
        setData(res.data.data || []);
      } catch (err) {
        console.error("Gagal memuat data kenaikan gaji:", err);
        console.error("Error detail:", err.response?.data);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authdata]);

  // Fungsi untuk menentukan status badge
  const getStatusBadge = (status, sisaWaktu) => {
    if (status === 'overdue' || sisaWaktu?.includes('lewat')) {
      return { colorScheme: 'red', icon: WarningIcon, label: 'TERLAMBAT' };
    }
    return { colorScheme: 'yellow', icon: TimeIcon, label: 'SEGERA' };
  };

  if (loading)
    return (
      <Center h="300px">
        <Spinner size="xl" color="green.500" />
      </Center>
    );

  return (
    <Box>
      {/* Alert Notifikasi */}
      {data.length > 0 && (
        <Alert
          status="info"
          bg={bgAlert}
          borderLeft="4px"
          borderColor="green.500"
          mb={4}
          rounded="md"
        >
          <AlertIcon as={BellIcon} color="green.600" />
          <Box flex="1">
            <AlertTitle color="green.800" fontWeight="bold">
              Pemberitahuan Kenaikan Gaji Berkala (KGB)
            </AlertTitle>
            <AlertDescription color="green.700">
              Terdapat <strong>{data.length} petugas</strong> yang sudah
              mencapai masa kerja <strong>1 tahun 10 bulan atau lebih</strong> sejak TMT Gaji terakhir.
              <br />
              Segera siapkan berkas untuk kenaikan gaji berkala (maksimal 2 tahun dari TMT Gaji terakhir)!
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Box p={6} bg={bgBox} rounded="xl" shadow="md">
        <HStack mb={4} justify="space-between">
          <Heading fontSize="xl" color={textColorPrimary}>
            Daftar Kenaikan Gaji Berkala (KGB)
          </Heading>
          {data.length > 0 && (
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              <Icon as={BellIcon} mr={1} />
              {data.length} Petugas
            </Badge>
          )}
        </HStack>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={bgHead}>
              <Tr>
                <Th color="white">No</Th>
                <Th color="white">Nama</Th>
                <Th color="white">Username</Th>
                <Th color="white">Jabatan</Th>
                <Th color="white">Pangkat/Golongan</Th>
                <Th color="white">TMT Gaji Terakhir</Th>
                <Th color="white">Umur Gaji</Th>
                <Th color="white">Deadline KGB (2 Tahun)</Th>
                <Th color="white">Sisa Waktu</Th>
                <Th color="white">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.length > 0 ? (
                data.map((item, index) => {
                  const statusInfo = getStatusBadge(item.status_kenaikan_gaji, item.sisa_waktu_kenaikan_gaji);
                  return (
                    <Tr key={index} _hover={{ bg: hoverBg }}>
                      <Td fontWeight="bold">{index + 1}</Td>
                      <Td fontWeight="semibold">{item.fullname?.toUpperCase() || "-"}</Td>
                      <Td>{item.username || "-"}</Td>
                      <Td>{item.jabatan || "-"}</Td>
                      <Td fontSize="xs">{item.pangkat_golongan || "-"}</Td>
                      <Td fontWeight="medium">{item.tmt_gaji_terakhir || "-"}</Td>
                      <Td>
                        <Badge colorScheme="blue" fontSize="xs">
                          {item.umur_gaji || "-"}
                        </Badge>
                      </Td>
                      <Td color="green.700" fontWeight="bold">
                        {item.kenaikan_gaji_berikutnya || "-"}
                      </Td>
                      <Td>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={statusInfo.colorScheme === 'red' ? 'red.600' : 'green.600'}
                        >
                          {item.sisa_waktu_kenaikan_gaji || "-"}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={statusInfo.colorScheme}
                          fontSize="xs"
                          px={2}
                          py={1}
                        >
                          <Icon as={statusInfo.icon} mr={1} />
                          {statusInfo.label}
                        </Badge>
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={10} textAlign="center" py={8}>
                    <Icon as={BellIcon} boxSize={12} color="green.400" mb={2} />
                    <Text color="green.600" fontSize="lg" fontWeight="bold">
                      ✅ Tidak Ada Notifikasi
                    </Text>
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      Semua petugas belum mencapai 1 tahun 10 bulan masa gaji
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}